import * as React from 'react'

import { Select as SelectPrimitive } from 'radix-ui'

import { cn } from './cn'

const EMPTY_SENTINEL = '__ps_empty__'

const encodeValue = (value: unknown) =>
  value === '' || value === null || value === undefined
    ? EMPTY_SENTINEL
    : String(value)

const decodeValue = (value: string) => (value === EMPTY_SENTINEL ? '' : value)

export interface SelectChangeEvent<T = string> {
  target: { value: T; name?: string }
}

export interface SelectProps {
  value?: unknown
  defaultValue?: unknown
  onChange?: (event: SelectChangeEvent) => void
  onOpen?: () => void
  onClose?: () => void
  /**
   * Accepted for MUI compatibility but not rendered. In MUI the visible label
   * comes from a sibling `<InputLabel>`; `Select`'s `label` only sizes the
   * outline notch. Rendering it here would double every label.
   */
  label?: React.ReactNode
  name?: string
  size?: 'small' | 'medium'
  fullWidth?: boolean
  disabled?: boolean
  error?: boolean
  required?: boolean
  displayEmpty?: boolean
  renderValue?: (value: unknown) => React.ReactNode
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  /** Accepted for MUI compatibility; unused (see `label` note above). */
  labelId?: string
  id?: string
  variant?: 'outlined' | 'filled' | 'standard'
  MenuProps?: unknown
  onClick?: React.MouseEventHandler
}

const Select = ({
  value,
  defaultValue,
  onChange,
  onOpen,
  onClose,
  label,
  name,
  size = 'medium',
  fullWidth,
  disabled,
  error,
  required,
  renderValue,
  className,
  style,
  children,
  labelId,
  id,
  variant,
  displayEmpty,
  MenuProps,
  onClick,
  ...rest
}: SelectProps) => (
  <div
    className={cn(
      'inline-flex flex-col align-top',
      fullWidth && 'w-full',
      className,
    )}
    style={style}
  >
    <SelectPrimitive.Root
      value={value !== undefined ? encodeValue(value) : undefined}
      defaultValue={
        defaultValue !== undefined ? encodeValue(defaultValue) : undefined
      }
      onValueChange={(v) =>
        onChange?.({ target: { value: decodeValue(v), name } })
      }
      onOpenChange={(open) => (open ? onOpen?.() : onClose?.())}
      disabled={disabled}
      name={name}
      {...rest}
    >
      <SelectPrimitive.Trigger
        id={id}
        onClick={onClick}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-field border bg-paper px-3 text-left text-sm text-text-primary shadow-paper',
          size === 'small' ? 'py-2' : 'py-3',
          'transition-all duration-150 focus:outline-none focus:border-primary/60 focus:shadow-glow-primary',
          error
            ? 'border-error/60'
            : 'border-divider-strong hover:border-text-disabled',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
          {renderValue ? (
            renderValue(value)
          ) : (
            <SelectPrimitive.Value placeholder="" />
          )}
        </span>
        <SelectPrimitive.Icon className="shrink-0 text-text-secondary">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-[1400] max-h-[40vh] min-w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-xl border border-divider bg-paper/95 p-1.5 text-text-primary shadow-menu backdrop-blur-md',
          )}
        >
          <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  </div>
)

export default Select

export interface MenuItemProps {
  value?: unknown
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  onClick?: React.MouseEventHandler
  children?: React.ReactNode
}

/** Option inside a Select (old MUI MenuItem). */
export const MenuItem = ({
  value,
  disabled,
  className,
  style,
  onClick,
  children,
}: MenuItemProps) => (
  <SelectPrimitive.Item
    value={encodeValue(value)}
    disabled={disabled}
    style={style}
    onClick={onClick}
    className={cn(
      'flex cursor-pointer select-none items-center rounded-lg px-3 py-2 text-sm outline-none transition-colors',
      'data-[highlighted]:bg-black/[0.05] dark:data-[highlighted]:bg-white/[0.07]',
      'data-[state=checked]:bg-primary/[0.12] data-[state=checked]:text-primary data-[state=checked]:font-medium',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      className,
    )}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)
